import { html, render } from 'lit';
import { msg } from '@omegagrid/localize';
import { customElement, property } from 'lit/decorators.js';
import { BaseVisualizationEditor } from './baseEditor';
import type { GaugeVisualization } from '../..';
import type { GaugeViewOptions } from '@reporting/rp-types/analytics';
import { choose } from 'lit/directives/choose.js';
import { ComponentStore, ComponentId, dom } from '@omegagrid/core';

@customElement('rp-visual-gauge-editor')
export class GaugeVisualizationEditor extends BaseVisualizationEditor<GaugeVisualization> {

	@property({ type: String })
	activeTab: 'data'|'props'|'series'|'json' = 'data';

	get viewOptions() {
		return super.viewOptions as GaugeViewOptions;
	}

	renderDataForm = () => html`
		<style>${BaseVisualizationEditor.styles}</style>

		<div class="row">
			<og-input
				class="field"
				data-key="value"
				data-type="string"
				@focus="${this._onFocus}"
				@change="${this._onDataChange}"
				.placeholder="${msg('Value')}"
				.value="${this.viewOptions?.value ?? ''}">
			</og-input>
		</div>

		<div class="row">
			<og-input
				class="field"
				data-key="min"
				data-type="string"
				@focus="${this._onFocus}"
				@change="${this._onDataChange}"
				.placeholder="${msg('Minimum')}"
				.value="${this.viewOptions?.min ?? ''}">
			</og-input>
		</div>

		<div class="row">
			<og-input
				class="field"
				data-key="max"
				data-type="string"
				@focus="${this._onFocus}"
				@change="${this._onDataChange}"
				.placeholder="${msg('Maximum')}"
				.value="${this.viewOptions?.max ?? ''}">
			</og-input>
		</div>

		<og-panel
			collapsible
			?collapsed="${false}"
			@resizestop="${() => {}}">

			<div class="header" slot="header">
				<og-icon icon="palette" fixed-width></og-icon> ${msg('Plot bands')}
			</div>

			<div class="body" slot="body" style="height: 100%; overflow: hidden">${this.renderPlotBandsForm()}</div>
		</og-panel>
	`;

	get gaugeStyle() {
		this.viewOptions.style ??= {};
		return this.viewOptions.style;
	}

	renderStyleForm = () => {
		const style = this.gaugeStyle;
		return html`
			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.angle"
					data-type="string"
					@change="${this._onDataChange}"
					.caption="${msg('Angle')}"
					.items="${[
						{k: 'half', v: msg('Half')},
						{k: 'three-quarter', v: msg('Three-quarter')},
						{k: 'full', v: msg('Full')},
					]}"
					.value="${style.angle ?? 'half'}">
				</og-dropdown-list>
			</div>

			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-dropdown-colorpicker
					class="field"
					color="transparent"
					data-key="style.dial_color"
					data-type="color"
					@change="${this._onDataChange}"
					.caption="${msg('Dial')}"
					.value="${style.dial_color}">
				</og-dropdown-colorpicker>

				<og-dropdown-colorpicker
					class="field"
					color="transparent"
					data-key="style.pivot_color"
					data-type="color"
					@change="${this._onDataChange}"
					.caption="${msg('Pivot')}"
					.value="${style.pivot_color}">
				</og-dropdown-colorpicker>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.dial_width"
					data-type="number"
					@change="${this._onDataChange}"
					.caption="${msg('Dial width')}"
					.items="${new Array(100).fill(0).map((_, i) => (i ? {k: i.toString(), v: i.toString()} : {k: null, v: 'auto'}))}"
					.value="${(style.dial_width ?? '').toString()}">
				</og-dropdown-list>
			</div>

			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-input
					class="field"
					data-key="style.tick_interval"
					data-type="number"
					@change="${this._onDataChange}"
					.placeholder="${msg('Tick interval')}"
					.value="${style.tick_interval ?? ''}">
				</og-input>

				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.minor_ticks"
					data-type="boolean"
					@change="${this._onDataChange}"
					.caption="${msg('Minor ticks')}"
					.items="${this.booleanItems()}"
					.value="${(style.minor_ticks !== false) ? '1' : '0'}">
				</og-dropdown-list>
			</div>

			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.labels"
					data-type="boolean"
					@change="${this._onDataChange}"
					.caption="${msg('Labels')}"
					.items="${this.booleanItems()}"
					.value="${(style.labels !== false) ? '1' : '0'}">
				</og-dropdown-list>

				<og-input
					class="field"
					data-key="style.labels_format"
					data-type="string"
					@change="${this._onDataChange}"
					.placeholder="${msg('Unit')}"
					.value="${style.labels_format ?? ''}">
				</og-input>
			</div>

			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.value_label"
					data-type="boolean"
					@change="${this._onDataChange}"
					.caption="${msg('Value')}"
					.items="${this.booleanItems()}"
					.value="${(style.value_label !== false) ? '1' : '0'}">
				</og-dropdown-list>

				<og-input
					class="field"
					data-key="style.value_format"
					data-type="string"
					@change="${this._onDataChange}"
					.placeholder="${msg('Unit')}"
					.value="${style.value_format ?? ''}">
				</og-input>
			</div>

			<div class="row" style="display: flex; flex-direction: row; gap: 4px">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.value_decimals"
					data-type="number"
					@change="${this._onDataChange}"
					.caption="${msg('Decimals')}"
					.items="${new Array(5).fill(0).map((_, i) => ({k: i.toString(), v: i.toString()}))}"
					.value="${(style.value_decimals ?? 0).toString()}">
				</og-dropdown-list>

				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.value_size"
					data-type="number"
					@change="${this._onDataChange}"
					.caption="${msg('Size')}"
					.items="${[0, 10, 12, 14, 16, 20, 24, 28, 32, 40].map(size => ({k: size.toString(), v: size ? size.toString() : 'auto'}))}"
					.value="${(style.value_size ?? 0).toString()}">
				</og-dropdown-list>

				<og-dropdown-colorpicker
					class="field"
					style="flex: 0 0 40px"
					color="transparent"
					data-key="style.value_color"
					data-type="color"
					@change="${this._onDataChange}"
					.value="${style.value_color}">
				</og-dropdown-colorpicker>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.band_thickness"
					data-type="number"
					@change="${this._onDataChange}"
					.caption="${msg('Band thickness')}"
					.items="${[0, 5, 10, 15, 20, 25, 30, 40, 50].map(v => ({k: v.toString(), v: v ? `${v} %` : 'auto'}))}"
					.value="${(style.band_thickness ?? 0).toString()}">
				</og-dropdown-list>
			</div>
		`;
	};

	renderPropsSection = () => html`
		<og-container style="height: 100%">
			<div slot="content">
				${this.renderStyleForm()}
			</div>
		</og-container>
	`;

	updated(props: Map<string, unknown>) {
		super.updated(props);
		if (props.has('reportContext') || props.has('result')) {
			this.loadDataGrid();
		}
		// monaco needs a relayout after the tab reattached it
		if (props.has('activeTab') && this.activeTab == 'json') {
			this.highchartOptionsEditor.layout();
		}
	}

	render = () => html`
		<div class="head">
			<og-bucket-button
				.text="${msg('Data').toUpperCase()}"
				icon="table"
				@click="${() => this.activeTab = 'data'}"
				?active="${this.activeTab == 'data'}">
			</og-bucket-button>

			<og-bucket-button
				.text="${msg('Chart properties').toUpperCase()}"
				icon="table"
				@click="${() => this.activeTab = 'props'}"
				?active="${this.activeTab == 'props'}">
			</og-bucket-button>

			<og-bucket-button
				.text="${msg('Highcharts').toUpperCase()}"
				icon="code"
				@click="${() => this.activeTab = 'json'}"
				?active="${this.activeTab == 'json'}">
			</og-bucket-button>
		</div>

		${choose(this.activeTab, [
			['data', () => this.renderDataSection()],
			['props', () => this.renderPropsSection()],
			['json', () => this.renderHighchartOptionsSection()],
		])}
	`;

}
